// src/components/AddItemForm.js
import React, { useState } from 'react';
import { addItem } from '../config/api';
import { Upload, DollarSign, Tag, FileText, ListFilter, ImagePlus } from 'lucide-react';
import { useStore } from '../context/StoreContext';

function AddItemForm() {
  const { categories, addItem: addItemToStore } = useStore();
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    price: '',
    category: '',
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.label || !formData.description || !formData.price || !image || !formData.category) {
      alert('All fields are required');
      return;
    }

    setLoading(true);

    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      submitData.append(key, formData[key]);
    });
    submitData.append('image', image);

    try {
      const newItem = await addItem(submitData);
      addItemToStore(newItem);
      
      // Reset form
      setFormData({
        name: '',
        label: '',
        description: '',
        price: '',
        category: '',
      });
      setImage(null);
      setPreview(null);
      alert('Item added successfully');
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden bg-white shadow-md rounded-xl">
      <div className="p-11">
        <h2 className="flex items-center mb-6 text-xl font-semibold text-amber-900 font-bebas">
          <Upload className="w-6 h-6 mr-2" />
          Add New Item
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-6">
              <div>
                <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                  <Tag className="w-4 h-4 mr-1" />
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required
                />
              </div>

              <div>
                <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                  <Tag className="w-4 h-4 mr-1" />
                  Label
                </label>
                <input
                  type="text"
                  name="label"
                  value={formData.label}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required
                />
              </div>

              <div>
                <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                  <DollarSign className="w-4 h-4 mr-1" />
                  Price
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required
                />
              </div>

              <div>
                <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                  <ListFilter className="w-4 h-4 mr-1" />
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div>
                <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                  <FileText className="w-4 h-4 mr-1" />
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required
                />
              </div>

              <div>
                <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                  <ImagePlus className="w-4 h-4 mr-1" />
                  Image
                </label>
                <div className="flex justify-center px-6 pt-5 pb-6 mt-1 border-2 border-dashed rounded-lg border-amber-200">
                  <div className="space-y-1 text-center">
                    {preview ? (
                      <div className="mb-4">
                        <img
                          src={preview}
                          alt="Preview"
                          className="object-cover w-32 h-32 mx-auto rounded-lg"
                        />
                      </div>
                    ) : (
                      <ImagePlus className="w-12 h-12 mx-auto text-gray-400" />
                    )}
                    <div className="flex text-sm text-gray-600">
                      <label className="relative font-medium bg-white rounded-md cursor-pointer text-amber-600 hover:text-amber-500">
                        <span>Upload a file</span>
                        <input
                          type="file"
                          className="sr-only"
                          onChange={handleImageChange}
                          accept="image/*"
                          required
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-6 py-3 text-white border border-transparent rounded-lg shadow-sm bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
            >
              <Upload className="w-5 h-5 mr-2" />
              {loading ? 'Adding...' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddItemForm;